/**
 * Error handling utilities for BhanzuBuddy chat
 *
 * Provides user-friendly error messages for different types of API errors
 * and network issues.
 */

/**
 * Helper function to generate polished error messages
 */
export const getErrorMessage = (error: unknown): string => {
  // Check if it's an axios error with response data
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response &&
    'status' in error.response
  ) {
    const response = error.response as {
      status: number;
      data: { message?: string };
    };

    // Check for rate limit errors
    if (
      response.status === 429 ||
      (response.data?.message &&
        typeof response.data.message === 'string' &&
        (response.data.message.includes('limit') ||
          response.data.message.includes('quota') ||
          response.data.message.includes('rate')))
    ) {
      return "Oops! 🌟 It looks like you've reached your daily question limit. Don't worry though - your curiosity is amazing! 🚀 Please try again tomorrow, and I'll be here ready to help you learn and grow. Take this time to review what we've discussed today! 📚✨";
    }

    // Check for server errors (5xx)
    if (response.status >= 500) {
      return "Oh no! 🤖 I'm having some technical difficulties on my end right now. Don't worry - it's not you, it's me! 😅 Please try asking your question again in a few minutes. I promise I'll be back to help you learn amazing things! 💫";
    }

    // Check for network or timeout errors
    if (response.status >= 400 && response.status < 500) {
      return "Hmm! 🤔 Something seems to have gone wrong with your request. Could you please try asking your question again? I'm here and ready to help you understand any topic you'd like! 🌟📖";
    }
  }

  // Check for network errors (no response)
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    typeof error.code === 'string' &&
    (error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED')
  ) {
    return "Oops! 📡 I'm having trouble connecting right now. Please check your internet connection and try again in a moment. I'll be right here waiting to help you learn! 🌐✨";
  }

  // Generic friendly error message
  return "Oh dear! 😅 Something unexpected happened, but don't let that stop your learning journey! Please try asking your question again - I'm excited to help you discover new things! 🎓💡";
};
